import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apolloClient from '@/lib/apollo-client';
import { DELETE_TRAINING, GET_TRAINING_ENROLLMENTS, MY_TRAININGS_QUERY } from '@/lib/graphql/queries';
import { updateEnrollmentStatus, UpdateEnrollmentInput } from '@/services/enrollmentService';

import { gql } from '@apollo/client';
import { Training, PaginationMeta, TrainingFilter } from './types';
import { FILTERED_TRAININGS_QUERY } from '@/lib/graphql/queries';

// GraphQL query for filtered trainings

// Initial filter state
const initialFilter: TrainingFilter = {
  search: null,
  category: null,
  level: null,
  format: null,
  certificate: null,
  location: null,
  minPrice: 0,
  maxPrice: null,
  page: 1,
  limit: 10,
};

// Async thunk for fetching filtered trainings
export const fetchFilteredTrainings = createAsyncThunk(
  'training/fetchFilteredTrainings',
  async (filter: TrainingFilter, { rejectWithValue }) => {
    try {
      console.log(filter)
      const { data } = await apolloClient.query({
        query: FILTERED_TRAININGS_QUERY,
        variables: {
          search: filter.search || null,
          category: filter.category || "",
          level: filter.level || "",
          format: filter.format || "",
          certificate: filter.certificate ,
          location: filter.location || "",
          minPrice: filter.minPrice ||  null,
          maxPrice: filter.maxPrice || null,
          page: filter.page || 1,
          limit: filter.limit || 10,
        },
        fetchPolicy: 'network-only',
      });

      return {
        trainings: data.filteredTrainings.trainings,
        meta: data.filteredTrainings.meta,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Training slice interface
interface TrainingState {
  trainingsList: Training[];
  paginationMeta: PaginationMeta | null;
  trainingsLoading: boolean;
  trainingsError: string | null;
  filter: TrainingFilter;
  // Add these new properties for enrollment
  enrollments: any[]; // Replace 'any' with a proper Enrollment interface if available
  enrollmentLoading: boolean;
  enrollmentError: string | null;
  enrollmentUpdateLoading: boolean;
  enrollmentUpdateError: string | null;
  deleteLoading: boolean;
  deleteError: string | null;
  // My trainings properties
  myTrainingsList: Training[];
  myTrainingsPaginationMeta: PaginationMeta | null;
  myTrainingsLoading: boolean;
  myTrainingsError: string | null;
  myTrainingsFilter: TrainingFilter;
}

// Initial state
const initialState: TrainingState = {
  trainingsList: [],
  paginationMeta: null,
  trainingsLoading: false,
  trainingsError: null,
  filter: initialFilter,
  // Initialize the new properties
  enrollments: [],
  enrollmentLoading: false,
  enrollmentError: null,
  enrollmentUpdateLoading: false,
  enrollmentUpdateError: null,
  deleteLoading: false,
  deleteError: null,
  // My trainings properties
  myTrainingsList: [],
  myTrainingsPaginationMeta: null,
  myTrainingsLoading: false,
  myTrainingsError: null,
  myTrainingsFilter: initialFilter,
};

// Create the training slice
// Add the enrollment mutation
export const ENROLL_IN_TRAINING = gql`
  mutation EnrollInTraining($trainingId: Int!) {
    enrollInTraining(trainingId: $trainingId) {
      id
      userId
      trainingId
      enrolledAt
      status

    }
  }
`;

// Create the async thunk for enrollment
export const enrollInTraining = createAsyncThunk(
  'training/enrollInTraining',
  async (trainingId: number, { rejectWithValue }) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation: ENROLL_IN_TRAINING,
        variables: { trainingId },
      });
      console.log("enrollment "+data)
      return data.enrollInTraining;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTrainingEnrollments = createAsyncThunk(
  'training/fetchTrainingEnrollments',
  async (filters: { trainingId: number; status?: string;search?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      console.log(JSON.stringify(filters))
      console.log(filters+ 'filter')
      const { data } = await apolloClient.query({
        query: GET_TRAINING_ENROLLMENTS,
        variables: filters,
        fetchPolicy: 'network-only', // Add this to ensure fresh data
      });
      console.log("enrollment "+data)
      console.log(JSON.stringify(data))

      return data.trainingEnrollments;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create the async thunk for updating enrollment status
export const updateEnrollmentStatusThunk = createAsyncThunk(
  'training/updateEnrollmentStatus',
  async (input: UpdateEnrollmentInput, { rejectWithValue, dispatch, getState }) => {
    try {
      console.log('Updating enrollment status with input:', input);

      // Ensure the ID is a number
      const sanitizedInput = {
        ...input,
        id: Number(input.id)
      };

      // Get the current state to access the training ID for refreshing enrollments if needed
      const state = getState() as { training: TrainingState };
      const enrollments = state.training.enrollments;
      const trainingId = enrollments.find(e => e.id === Number(input.id))?.trainingId || 0;

      // Call the API to update the enrollment status
      const result = await updateEnrollmentStatus(sanitizedInput);
      console.log('Enrollment status update result:', result);

      // Return both the result and the training ID for potential use in the fulfilled action
      return {
        enrollment: result,
        trainingId,
        currentPage: state.training.paginationMeta?.currentPage || 1,
        itemsPerPage: state.training.paginationMeta?.itemsPerPage || 10
      };
    } catch (error) {
      console.error('Error updating enrollment status:', error);
      return rejectWithValue(error.message || 'Failed to update enrollment status');
    }
  }
);

// Async thunk for fetching my trainings
export const fetchMyTrainings = createAsyncThunk(
  'training/fetchMyTrainings',
  async (filter: TrainingFilter, { rejectWithValue }) => {
    try {
      const { data } = await apolloClient.query({
        query: MY_TRAININGS_QUERY,
        variables: {
          search: filter.search || null,
          category: filter.category || "",
          level: filter.level || "",
          format: filter.format || "",
          certificate: filter.certificate,
          location: filter.location || "",
          minPrice: filter.minPrice || null,
          maxPrice: filter.maxPrice || null,
          page: filter.page || 1,
          limit: filter.limit || 10,
        },
        fetchPolicy: 'network-only',
      });

      return {
        trainings: data.myTrainings.trainings,
        meta: data.myTrainings.meta,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch my trainings');
    }
  }
);

export const deleteTraining = createAsyncThunk(
  'training/deleteTraining',
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation:DELETE_TRAINING,
        variables: { id },
      });
      return { id, success: data.deleteTraining };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete training');
    }
  }
);

const trainingSlice = createSlice({
  name: 'training',
  initialState,
  reducers: {
    setTrainingFilter: (state, action: PayloadAction<Partial<TrainingFilter>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    resetTrainingFilter: (state) => {
      state.filter = initialFilter;
    },
    setMyTrainingsFilter: (state, action: PayloadAction<Partial<TrainingFilter>>) => {
      state.myTrainingsFilter = { ...state.myTrainingsFilter, ...action.payload };
    },
    resetMyTrainingsFilter: (state) => {
      state.myTrainingsFilter = initialFilter;
    },
    // Add a new reducer to handle local enrollment updates
    updateEnrollmentSuccess: (state, action: PayloadAction<any[]>) => {
      state.enrollments = action.payload;
      state.enrollmentUpdateLoading = false;
      state.enrollmentUpdateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFilteredTrainings.pending, (state) => {
        state.trainingsLoading = true;
        state.trainingsError = null;
      })
      .addCase(fetchFilteredTrainings.fulfilled, (state, action) => {
        state.trainingsLoading = false;
        state.trainingsList = action.payload.trainings;
        state.paginationMeta = action.payload.meta;
      })
      .addCase(fetchFilteredTrainings.rejected, (state, action) => {
        state.trainingsLoading = false;
        state.trainingsError = action.payload as string;
      });

    // Add enrollment handling
    builder
      .addCase(enrollInTraining.pending, (state) => {
        state.enrollmentLoading = true;
        state.enrollmentError = null;
      })
      .addCase(enrollInTraining.fulfilled, (state, action) => {
        state.enrollmentLoading = false;
        state.enrollments.push(action.payload);
        // Update training in trainingsList
        const trainingIndex = state.trainingsList.findIndex(
          (t) => t.id === action.payload.trainingId
        );
        if (trainingIndex !== -1) {
          state.trainingsList[trainingIndex].enrollments =
            (state.trainingsList[trainingIndex].enrollments || 0) + 1;
        }
      })
      .addCase(enrollInTraining.rejected, (state, action) => {
        state.enrollmentLoading = false;
        state.enrollmentError = action.payload as string;
      });

    // Add these cases for fetchTrainingEnrollments
    builder
      .addCase(fetchTrainingEnrollments.pending, (state) => {
        state.enrollmentLoading = true;
        state.enrollmentError = null;
      })
      .addCase(fetchTrainingEnrollments.fulfilled, (state, action) => {
        state.enrollmentLoading = false;
        // Make sure we're setting the enrollments array directly from the payload
        state.enrollments = action.payload.enrollments || [];
        state.paginationMeta = action.payload.meta;
      })
      .addCase(fetchTrainingEnrollments.rejected, (state, action) => {
        state.enrollmentLoading = false;
        state.enrollmentError = action.payload as string;
      })
      // Add cases for updateEnrollmentStatusThunk
      .addCase(updateEnrollmentStatusThunk.pending, (state) => {
        state.enrollmentUpdateLoading = true;
        state.enrollmentUpdateError = null;
      })
      .addCase(updateEnrollmentStatusThunk.fulfilled, (state, action) => {
        state.enrollmentUpdateLoading = false;

        // Update the specific enrollment in the state
        if (action.payload?.enrollment) {
          const updatedEnrollment = action.payload.enrollment;
          const index = state.enrollments.findIndex(e => e.id === updatedEnrollment.id);

          if (index !== -1) {
            // Replace the enrollment with the updated one
            state.enrollments[index] = {
              ...state.enrollments[index],
              ...updatedEnrollment
            };
          }
        }
      })
      .addCase(updateEnrollmentStatusThunk.rejected, (state, action) => {
        state.enrollmentUpdateLoading = false;
        state.enrollmentUpdateError = action.payload as string;
      });

      builder.addCase(deleteTraining.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteTraining.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const deletedId = action.payload.id;
        // Remove the deleted training from the list
        state.trainingsList = state.trainingsList.filter(
          (training) => training.id !== deletedId
        );
        // Update pagination metadata if available
        if (state.paginationMeta) {
          state.paginationMeta.totalItems = Math.max(
            0,
            (state.paginationMeta.totalItems || 0) - 1
          );
          state.paginationMeta.itemCount = state.trainingsList.length;
          if (state.paginationMeta.itemsPerPage) {
            state.paginationMeta.totalPages = Math.ceil(
              state.paginationMeta.totalItems / state.paginationMeta.itemsPerPage
            );
          }
        }
      })
      .addCase(deleteTraining.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      })

      // Add cases for fetchMyTrainings
      .addCase(fetchMyTrainings.pending, (state) => {
        state.myTrainingsLoading = true;
        state.myTrainingsError = null;
      })
      .addCase(fetchMyTrainings.fulfilled, (state, action) => {
        state.myTrainingsLoading = false;
        state.myTrainingsList = action.payload.trainings;
        state.myTrainingsPaginationMeta = action.payload.meta;
      })
      .addCase(fetchMyTrainings.rejected, (state, action) => {
        state.myTrainingsLoading = false;
        state.myTrainingsError = action.payload as string;
      });
  },
});

export const {
  setTrainingFilter,
  resetTrainingFilter,
  setMyTrainingsFilter,
  resetMyTrainingsFilter,
  updateEnrollmentSuccess
} = trainingSlice.actions;
export default trainingSlice.reducer;